/**
 * A signed bay declaration shown as the form the keeper filled (F5.4-g): each question of the
 * form next to the answer given, risky answers flagged. The form comes from uc-core code at its
 * current version; answers to questions no longer on it are still listed.
 */
import type { BayDeclarationRow, DeclarationFormDef } from '../api/store'
import { humanize } from '../labels'

export interface AnswerRow {
  key: string
  question: string
  questionHi: string | null
  answer: string
  risky: boolean
  /** Required (and applicable) but not answered. */
  missing: boolean
}

export function formatAnswer(value: unknown): string {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

export function declarationAnswerRows(form: DeclarationFormDef | null, answers: Record<string, unknown>): AnswerRow[] {
  const rows: AnswerRow[] = []
  const seen = new Set<string>()
  for (const q of form?.questions ?? []) {
    seen.add(q.key)
    const value = answers[q.key]
    const applies = !q.requiredWhen || answers[q.requiredWhen.key] === q.requiredWhen.equals
    if (!applies && value === undefined) continue
    rows.push({
      key: q.key,
      question: q.labelEn,
      questionHi: q.labelHi,
      answer: formatAnswer(value),
      risky: value !== undefined && q.riskyAnswers.some((r) => r === value),
      missing: q.required && applies && (value === undefined || value === null),
    })
  }
  for (const [key, value] of Object.entries(answers)) {
    if (seen.has(key)) continue
    rows.push({ key, question: humanize(key), questionHi: null, answer: formatAnswer(value), risky: false, missing: false })
  }
  return rows
}

const yesNo = (v: boolean | null) => (v === null ? '' : v ? 'yes' : 'no')

/** Auditor export: one row per declaration, the answers as their own columns. */
export function declarationsCsv(rows: readonly BayDeclarationRow[]): { header: string[]; rows: string[][] } {
  const answerKeys = [...new Set(rows.flatMap((r) => Object.keys(r.answers)))].sort()
  const header = [
    'bay_code',
    'bay',
    'form',
    'form_version',
    'signed_by',
    'signed_at',
    'valid_to',
    'clean',
    'in_force',
    'latest',
    'photo_id',
    'photo_arrived',
    ...answerKeys,
  ]
  return {
    header,
    rows: rows.map((r) => [
      r.bayCode,
      r.bayPath,
      r.formCode,
      String(r.formVersion),
      r.signedByName ?? r.signedById ?? '',
      r.signedAt ?? '',
      r.validTo ?? '',
      yesNo(r.clean),
      yesNo(r.inForce),
      yesNo(r.latest),
      r.photo ?? '',
      yesNo(r.photoArrived),
      ...answerKeys.map((k) => (k in r.answers ? formatAnswer(r.answers[k]) : '')),
    ]),
  }
}
